import React, {createContext, useContext} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {useHistory} from "react-router-dom";
import {showGlobalMessage} from '../../_common/components/GlobalMessage/actions/showGlobalMessage';
import {messages as M} from '../../../../_metronic/i18n/messages';
import * as orderActions from '../../OrderDetails/_redux/actions/OrderDetailsActions';
import * as tempInspectionsActions from '../../PriceQuoteDetails/_redux/actions/tempInspectionsActions';
import * as inspectionsActions from '../../_common/actions/orderInspectionsActions';
import {Customer} from "../../_common/types";
import {Inspection} from "../../PriceQuoteDetails/contexts/TempInspectionsListContext";
import moment from "moment/moment";

type PopupCreateOrderContext = {
  handleCreateOrder: () => Promise<void>
  gotLines: boolean
}

type InspectionWithLocation = Inspection & {
  cityID: number;
  streetID: number;
};

type CreateInspectionsFn = (
  tempInspections: InspectionWithLocation[],
  orderID: number
) => Promise<void>;

const PopupCreateOrderContext = createContext<PopupCreateOrderContext>({} as PopupCreateOrderContext);

export function usePopupCreateOrderContext() {
  return useContext(PopupCreateOrderContext);
}

type PriceQuoteRow = {
  contactAddress: string
  contactDeleted: number
  contactEmail: string
  contactFirstName: string
  contactID: number
  contactJob: string
  contactLastName: string
  contactOrganization: string
  contactPhone: string
  createDate: string
  customers: Customer[]
  fileName: string
  orderID: number
  price: string
  priceQuoteID: number
  priceQuoteStatusID: number
  priceQuoteStatusName: string
  sentDate: string
  sentTo: string
  lines: {
    description: string
    lineID: number
    price: number
    quantity: number
    vatType: number
  }[]
}

interface PopupCreateOrderProviderProps {
  children: React.ReactNode;
  cellContent: PriceQuoteRow
}

export function PopupCreateOrderProvider({children, cellContent}: PopupCreateOrderProviderProps) {
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    priceQuoteID,
    customers,
    contactID,
    price,
    lines
  } = cellContent;

  const gotLines = Array.isArray(lines) ? lines.length > 0 : false;

  const handleCreateOrder = async (): Promise<void> => {
    try {
      const tempInspections = await tempInspectionsActions.getTempInspectionsList(
        priceQuoteID
      ) as InspectionWithLocation[];

      const orderData = {
        orderID: -1,
        customers: Array.isArray(customers) ? customers.map(item => item.customerID) : [],
        shumaForOrderCount:
          tempInspections?.length > 0 ? tempInspections.length : 1,
        usingLawyer: false,
        lawyerName: '',
        dueDate: '',
        responsibleUser: null,
        priceQuoteID,
        paid: 0,
        totalPayment: price,
        contactID,
      };

      const orderID = await orderActions.createEditOrder(orderData);

      if (!orderID) {
        throw new Error('Order creation failed');
      }

      await createInspectionsFromTemp(tempInspections, orderID);

      history.push('/orders-page');
    } catch (e) {
      console.error(e);
      dispatch(showGlobalMessage('error', M['-1']));
    }
  };

  const createInspectionsFromTemp: CreateInspectionsFn = async (
    tempInspections,
    orderID
  ) => {
    try {
      await Promise.all(
        tempInspections.map(async (item) => {
          const {
            shumaReasonID,
            shumaTypeID,
            propertyTypeID,
            bankID,
            loanTypeID,
            loanText,
            otherPropertyTypeID,
            bankBranchNumber,
            bankBranchName,
            bankClerkFullName,
            bankEmail,
            bankRequestID,
            bankBuildType,
            dueDate,
            cityID,
            streetID,
            houseNumber,
            apartmentNumber,
            gush,
            helka,
            ttHelka,
            notes,
            landLotNum,
            landLotNumAccordingTaba,
            responsibleUser,
            loanSubTypeID,
            shumaMazminIdentityID,
            underConstruction,
          } = item;

          const data = {
            orderID,
            shumaID: -1,
            shumaReasonID,
            shumaTypeID,
            propertyTypeID,
            bankID,
            loanTypeID,
            loanText,
            otherPropertyTypeID,
            bankBranchNumber,
            bankBranchName,
            bankClerkFullName,
            bankEmail,
            bankRequestID,
            bankBuildType,
            dueDate: dueDate ? moment(dueDate).format('YYYY-MM-DD') : null,
            city: cityID,
            street: streetID,
            houseNumber,
            apartmentNumber,
            gush,
            helka,
            ttHelka,
            notes,
            landLotNum,
            landLotNumAccordingTaba,
            responsibleUser,
            originTypeID: 1,
            loanSubTypeID,
            shumaMazminIdentityID,
            underConstruction,
          };

          dispatch(inspectionsActions.addEditShumaLinkedToOrder(data));
        })
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  return (
    <PopupCreateOrderContext.Provider value={{handleCreateOrder, gotLines}}>
      {children}
    </PopupCreateOrderContext.Provider>
  );
}

PopupCreateOrderProvider.propTypes = {
  children: PropTypes.node,
};